import React, { useState, useEffect, useRef } from 'react';
import {
  Package,
  MapPin,
  Scan,
  Plus,
  CheckCircle,
  AlertCircle,
  Loader,
  Save,
  RotateCcw,
  Edit2,
  Trash2,
  X,
  Check,
  Search,
  Settings,
  Lock,
  Unlock,
  Hash,
  ClipboardList
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import {
  inventoryService,
  InventorySession,
  InventoryCountLine,
  InventoryFormData,
  ItemLocationInfo
} from '../../../../services/inventoryService';
import { ItemSearchModal, LocationInfo } from '../../../common/ItemSearchModal';
import { LocationSearchModal, LocationSearchResult } from '../../../common/LocationSearchModal';
import { ItemsDto } from '../../../../types/items';
import { SettingsModal } from './SettingsModal';
import { SerialCountingModal, SerialCountResult } from './modals/SerialCountingModal';
import { BatchCountingModal, BatchCountResult } from './modals/BatchCountingModal';
import { settingsService } from '../../../../services/settingsService';
import { parseGS1Barcode, GS1Data } from '../../../../utils/gs1Parser';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { CountingListSelector } from './CountingListSelector';
import { CountingListCounting } from './CountingListCounting';
import { CountingListOverviewItem } from '../../../../types/countingListCreation';

interface InventoryAppProps {
  onBack: () => void;
}

interface EditingLine {
  lineID: number;
  quantity: string;
}

type CountingMode = 'free-counting' | 'counting-list';

export const InventoryApp: React.FC<InventoryAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();

  // Counting mode: free counting vs predefined counting list
  const [countingMode, setCountingMode] = useState<CountingMode>('free-counting');
  const [selectedCountingList, setSelectedCountingList] = useState<CountingListOverviewItem | null>(null);

  const [session, setSession] = useState<InventorySession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false); // Separate loading for session operations
  const [formData, setFormData] = useState<InventoryFormData>({
    scannedCode: '',
    quantity: '',
    isScanning: false,
    validationMessage: '',
    validationType: ''
  });
  const [currentItem, setCurrentItem] = useState<{ itemNo: string; description: string; itemType?: number; } | null>(null);
  const [currentLocation, setCurrentLocation] = useState<{ locationName: string; } | null>(null);
  const [showSerialCountingModal, setShowSerialCountingModal] = useState(false);
  const [showBatchCountingModal, setShowBatchCountingModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingLine, setEditingLine] = useState<EditingLine | null>(null);
  const [deletingLineId, setDeletingLineId] = useState<number | null>(null);
  const [updatingLineId, setUpdatingLineId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState<string>('');

  // Rapid scanning settings
  const [enableRapidScanning, setEnableRapidScanning] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isLocationLocked, setIsLocationLocked] = useState(false);
  const [lockedLocation, setLockedLocation] = useState<{ locationName: string; } | null>(null);

  // State for search modals
  const [showItemSearchModal, setShowItemSearchModal] = useState(false);
  const [showLocationSearchModal, setShowLocationSearchModal] = useState(false);
  
  const scanInputRef = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const editQuantityInputRef = useRef<HTMLInputElement>(null);
  const sessionInitialized = useRef(false); // Prevent multiple initialization

  useEffect(() => {
    if (!sessionInitialized.current) {
      loadInventorySession();
      sessionInitialized.current = true;
    }
  }, [user?.operatorName]);

  // Auto-focus disabled - global scanning is active
  // useEffect(() => {
  //   if (scanInputRef.current && !loading) {
  //     scanInputRef.current.focus();
  //   }
  // }, [loading]);

  useEffect(() => {
    // Auto-focus på edit input når editing mode starter
    if (editingLine && editQuantityInputRef.current) {
      editQuantityInputRef.current.focus();
      editQuantityInputRef.current.select();
    }
  }, [editingLine]);

  // Filter tellinger basert på varenummer og lokasjonskoder
  const filteredLines = session?.lines.filter((line: InventoryCountLine) => {
    if (!filterText.trim()) return true;

    const searchTerm = filterText.toLowerCase();
    const itemNo = line.itemNo.toLowerCase();
    const locationName = line.locationName.toLowerCase();
    const description = (line.description1 || '').toLowerCase();

    const serialNo = (line.itemSerialNo || '').toLowerCase();

    return itemNo.includes(searchTerm) ||
           locationName.includes(searchTerm) ||
           description.includes(searchTerm) ||
           serialNo.includes(searchTerm);
  }) || [];

  // Detect if any modal is open to disable global scanning
  const isAnyModalOpen = showItemSearchModal || showLocationSearchModal || showSettingsModal || showSerialCountingModal || showBatchCountingModal;

  // Check if global barcode scanning is enabled via system setting
  const barcodeScannerEnabled = useBarcodeScannerEnabled();

  // Global barcode scanner - processes scans without requiring input focus
  useSimpleBarcodeScanner({
    enabled: barcodeScannerEnabled && !isAnyModalOpen,
    onScan: (barcode: string) => {
      processGlobalBarcode(barcode);
    }
  });

  // Process global barcode scan - replicates input box parsing logic
  const processGlobalBarcode = async (value: string) => {
    if (!value.trim()) return;

    // Prevent double-scanning - ignore new scans while processing
    if (formData.isScanning) {
      console.log('⏸️ Already processing scan, ignoring new input (global)');
      return;
    }

    // Call the existing handleScanInput function which already has all the logic
    await handleScanInput(value);
  };

  const loadInventorySession = async () => {
    if (!user?.operatorName || sessionLoading) return; // Forhindre multiple kall

    setSessionLoading(true);
    setLoading(true);

    try {
      // Load rapid scanning setting from ProfileSetting (per user)
      try {
        const lockLocationSetting = await settingsService.getProfileSetting('inventory.lock_location');
        if (lockLocationSetting?.value) {
          const isEnabled = lockLocationSetting.value === 'true' || lockLocationSetting.value === '1';
          setEnableRapidScanning(isEnabled);
          console.log('Loaded inventory.lock_location profile setting:', isEnabled);
        }
      } catch (err) {
        console.warn('Could not load inventory.lock_location setting, using default (false):', err);
      }

      console.log('Loading inventory session for operator:', user.operatorName);

      // Søk etter eksisterende aktiv session
      const activeSession = await inventoryService.getActiveInventorySession(user.operatorName);
      
      if (activeSession) {
        console.log('Found existing active session:', activeSession.batchID);
        setSession(activeSession);
      } else {
        console.log('No active session found, creating new one');
        // Opprett ny session første gang
        const newSession = await inventoryService.createInventorySession(user.operatorName, currentWarehouse || '1');
        console.log('Created new session:', newSession.batchID);
        setSession(newSession);
      }
    } catch (error) {
      console.error('Feil ved lasting av tellesession:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.loadSessionFailed'),
        validationType: 'error'
      }));
    } finally {
      setSessionLoading(false);
      setLoading(false);
    }
  };

  const handleScanInput = async (value: string) => {
    if (!value.trim()) return;

    // Prevent double-scanning - ignore new scans while processing
    if (formData.isScanning) {
      console.log('⏸️ Already processing scan, ignoring new input');
      return;
    }

    setFormData((prev: InventoryFormData) => ({ ...prev, isScanning: true, validationMessage: '' }));

    try {
      // Check if input looks like GS1-128 (contains separators or has ]C1 prefix)
      const looksLikeGS1 = value.includes(String.fromCharCode(29)) || value.includes('~') || value.includes('§') || value.startsWith(']C1');

      console.log('Raw scan input:', value, 'Looks like GS1:', looksLikeGS1);

      let scannedItemNo = value; // Default to raw value
      let scannedQuantity: number | undefined = undefined;

      if (looksLikeGS1) {
        // Try parsing as GS1-128
        const gs1Data = parseGS1Barcode(value);
        console.log('GS1 parse result:', gs1Data);

        // Check if we got valid GS1 data with GTIN
        const hasGS1Data = gs1Data.parsedAIs && Object.keys(gs1Data.parsedAIs).length > 0;

        if (hasGS1Data && gs1Data.gtin) {
          // GS1-128 barcode with GTIN
          console.log('✓ GS1-128 format detected');
          scannedItemNo = gs1Data.gtin;
          scannedQuantity = gs1Data.quantity;
        } else {
          // Had separators but no valid GS1 - strip separators
          console.log('✓ Had separators but not valid GS1, stripping separators');
          scannedItemNo = value.replace(/[~§]+$/g, '').trim();
        }
      } else {
        // Plain barcode (just item number or location)
        console.log('✓ Plain barcode format detected');
        // Strip scanner-specific separator characters from plain barcodes
        scannedItemNo = value.replace(/[~§]+$/g, '').trim();
      }

      // RAPID SCANNING MODE: Auto-increment quantity for matching item at locked location
      if (enableRapidScanning && isLocationLocked && lockedLocation) {
        // In rapid scanning mode, scanned value should be an item
        const itemValidation = await inventoryService.validateItem(scannedItemNo);

        if (!itemValidation.valid) {
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.inventory.errors.itemNotFound', { '0': scannedItemNo }),
            validationType: 'error'
          }));
          // Auto-focus disabled - global scanning is active
          // setTimeout(() => scanInputRef.current?.focus(), 100);
          return;
        }

        // Check if a count line already exists for this item + location
        const existingLine = session?.lines.find(
          line => line.itemNo?.toLowerCase() === itemValidation.itemNo.toLowerCase() &&
                  line.locationName?.toLowerCase() === lockedLocation.locationName.toLowerCase()
        );

        // Use quantity from GS1 if available, otherwise default to 1
        const quantityToAdd = scannedQuantity || 1;

        if (existingLine) {
          // Increment quantity
          const newQuantity = existingLine.quantity + quantityToAdd;
          try {
            const updatedLine = await inventoryService.updateCountLine(session!.batchID, existingLine.lineID, newQuantity);

            // Oppdater session med oppdatert linje
            setSession((prev: InventorySession | null) => prev ? {
              ...prev,
              lines: prev.lines.map(line =>
                line.lineID === existingLine.lineID ? updatedLine : line
              )
            } : null);

            setFormData((prev: InventoryFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventory.messages.quantityUpdated', { '0': itemValidation.itemNo, '1': newQuantity }),
              validationType: 'success'
            }));
          } catch (error) {
            console.error('Feil ved oppdatering av tellelinje:', error);
            setFormData((prev: InventoryFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventory.errors.updateQuantityFailed'),
              validationType: 'error'
            }));
          }
        } else {
          // Create new count line with scanned quantity
          try {
            const countData = {
              itemNo: itemValidation.itemNo,
              locationName: lockedLocation.locationName,
              quantity: quantityToAdd,
              description1: itemValidation.description1 || '',
              warehouse: session!.warehouse
            };

            const newLine = await inventoryService.addCountLine(session!.batchID, countData);

            // Oppdater session med ny linje
            setSession((prev: InventorySession | null) => prev ? {
              ...prev,
              lines: [newLine, ...prev.lines],
              transactionCount: prev.transactionCount + 1
            } : null);

            setFormData((prev: InventoryFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventory.messages.quantityUpdatedNew', { '0': itemValidation.itemNo, '1': quantityToAdd }),
              validationType: 'success'
            }));
          } catch (error) {
            console.error('Feil ved adding av tellelinje:', error);
            setFormData((prev: InventoryFormData) => ({
              ...prev,
              isScanning: false,
              validationMessage: t('warehouse.modules.inventory.errors.addCountLineFailed'),
              validationType: 'error'
            }));
          }
        }

        // Auto-focus disabled - global scanning is active
        // setTimeout(() => scanInputRef.current?.focus(), 100);
        return;
      }

      // NORMAL MODE: Try as item first
      const itemValidation = await inventoryService.validateItem(scannedItemNo);
      if (itemValidation.valid) {
        setCurrentItem({
          itemNo: itemValidation.itemNo,
          description: itemValidation.description1 || '',
          itemType: itemValidation.itemType
        });

        // If GS1 quantity is present, set it in the form
        if (typeof scannedQuantity === 'number' && scannedQuantity > 0) {
          const qty = scannedQuantity; // Type narrowing
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            quantity: qty.toString(),
            isScanning: false,
            validationMessage: t('warehouse.modules.inventory.messages.itemFoundWithQuantity', { '0': itemValidation.description1 || '', '1': qty }),
            validationType: 'success'
          }));
        } else {
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            isScanning: false,
            validationMessage: t('warehouse.modules.inventory.messages.itemFound', { '0': itemValidation.description1 || '' }),
            validationType: 'success'
          }));
        }

        // If both item and location are scanned, focus on quantity (unless GS1 has quantity)
        // Otherwise focus back to scanner
        if (currentLocation) {
          // If GS1 provided quantity and we have both item and location, we can add directly
          if (typeof scannedQuantity === 'number' && scannedQuantity > 0) {
            // Auto-submit if we have all data
            setTimeout(() => handleAddCount(), 100);
          } else {
            setTimeout(() => quantityInputRef.current?.focus(), 100);
          }
        } else {
          // Auto-focus disabled - global scanning is active
          // setTimeout(() => scanInputRef.current?.focus(), 100);
        }
        return;
      }

      // Try as location - use the same cleaned scannedItemNo value
      const locationValidation = await inventoryService.validateLocation(scannedItemNo);
      if (locationValidation.valid) {
        setCurrentLocation({
          locationName: locationValidation.locationName
        });
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          isScanning: false,
          validationMessage: t('warehouse.modules.inventory.messages.locationFound', { '0': locationValidation.locationName }),
          validationType: 'success'
        }));
        // If both item and location are scanned, focus on quantity (unless GS1 already set quantity)
        // Otherwise focus back to scanner
        if (currentItem) {
          // If GS1 provided quantity and we have both item and location, we can add directly
          if (formData.quantity && parseFloat(formData.quantity) > 0) {
            setTimeout(() => handleAddCount(), 100);
          } else {
            setTimeout(() => quantityInputRef.current?.focus(), 100);
          }
        } else {
          // Auto-focus disabled - global scanning is active
          // setTimeout(() => scanInputRef.current?.focus(), 100);
        }
        return;
      }

      // No match found
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: t('warehouse.modules.inventory.errors.barcodeNotFound', { '0': value }),
        validationType: 'error'
      }));
      // Auto-focus disabled - global scanning is active
      // setTimeout(() => scanInputRef.current?.focus(), 100);

    } catch (error) {
      console.error('Feil ved validering:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        isScanning: false,
        validationMessage: t('warehouse.modules.inventory.errors.validationFailed'),
        validationType: 'error'
      }));
      // Auto-focus disabled - global scanning is active
      // setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  const handleAddCount = async () => {
    if (!session || !currentItem || !currentLocation) {
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.scanItemAndLocationFirst'),
        validationType: 'error'
      }));
      return;
    }

    const quantity = parseFloat(formData.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.validQuantityRequired'),
        validationType: 'error'
      }));
      // Fokus på quantity ved feil
      setTimeout(() => quantityInputRef.current?.focus(), 100);
      return;
    }

    setIsAdding(true);
    try {
      const countData = {
        itemNo: currentItem.itemNo,
        locationName: currentLocation.locationName,
        quantity,
        description1: currentItem.description,
        warehouse: session.warehouse
      };

      const newLine = await inventoryService.addCountLine(session.batchID, countData);
      
      // Oppdater session med ny linje
      setSession((prev: InventorySession | null) => prev ? {
        ...prev,
        lines: [newLine, ...prev.lines],
        transactionCount: prev.transactionCount + 1
      } : null);

      // Reset form for neste telling
      setFormData({
        scannedCode: '',
        quantity: '',
        isScanning: false,
        validationMessage: t('warehouse.modules.inventory.messages.countAdded', { '0': currentItem.itemNo, '1': quantity, '2': currentLocation.locationName }),
        validationType: 'success'
      });
      setCurrentItem(null);
      setCurrentLocation(null);

      // Auto-focus disabled - global scanning is active
      // setTimeout(() => scanInputRef.current?.focus(), 100);

    } catch (error) {
      console.error('Feil ved adding av tellelinje:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.addCountLineFailed'),
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  const handleStartEdit = (line: InventoryCountLine) => {
    setEditingLine({
      lineID: line.lineID,
      quantity: line.quantity.toString()
    });
  };

  const handleCancelEdit = () => {
    setEditingLine(null);
  };

  const handleSaveEdit = async () => {
    if (!editingLine || !session) return;

    const quantity = parseFloat(editingLine.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.validQuantityRequired'),
        validationType: 'error'
      }));
      return;
    }

    setUpdatingLineId(editingLine.lineID);
    try {
      const updatedLine = await inventoryService.updateCountLine(session.batchID, editingLine.lineID, quantity);
      
      // Oppdater session med oppdatert linje
      setSession((prev: InventorySession | null) => prev ? {
        ...prev,
        lines: prev.lines.map(line => 
          line.lineID === editingLine.lineID ? updatedLine : line
        )
      } : null);

      setEditingLine(null);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.messages.quantityUpdatedSimple', { '0': quantity }),
        validationType: 'success'
      }));

      // Clear message after 3 seconds
      setTimeout(() => {
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          validationMessage: '',
          validationType: ''
        }));
      }, 3000);

    } catch (error) {
      console.error('Feil ved oppdatering av tellelinje:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: 'Kunne ikke oppdatere tellelinje',
        validationType: 'error'
      }));
    } finally {
      setUpdatingLineId(null);
    }
  };

  const handleDeleteLine = async (line: InventoryCountLine) => {
    if (!session) return;

    const confirmDelete = window.confirm(
      t('warehouse.modules.inventory.confirmations.deleteCount', { '0': line.itemNo, '1': line.description1, '2': line.locationName, '3': line.quantity })
    );
    if (!confirmDelete) return;

    setDeletingLineId(line.lineID);
    try {
      const success = await inventoryService.deleteCountLine(session.batchID, line.lineID);
      
      if (success) {
        // Fjern linje fra session
        setSession((prev: InventorySession | null) => prev ? {
          ...prev,
          lines: prev.lines.filter(l => l.lineID !== line.lineID),
          transactionCount: prev.transactionCount - 1
        } : null);

        setFormData((prev: InventoryFormData) => ({
          ...prev,
          validationMessage: t('warehouse.modules.inventory.messages.countDeleted', { '0': line.itemNo }),
          validationType: 'success'
        }));

        // Clear message after 3 seconds
        setTimeout(() => {
          setFormData((prev: InventoryFormData) => ({
            ...prev,
            validationMessage: '',
            validationType: ''
          }));
        }, 3000);
      }

    } catch (error) {
      console.error('Feil ved sletting av tellelinje:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.deleteCountLineFailed'),
        validationType: 'error'
      }));
    } finally {
      setDeletingLineId(null);
    }
  };

  const handleReset = () => {
    setFormData({
      scannedCode: '',
      quantity: '',
      isScanning: false,
      validationMessage: '',
      validationType: ''
    });
    setCurrentItem(null);
    setCurrentLocation(null);
    // Auto-focus disabled - global scanning is active
    // setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleToggleLocationLock = () => {
    if (isLocationLocked) {
      // Unlock
      setIsLocationLocked(false);
      setLockedLocation(null);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.messages.locationUnlocked'),
        validationType: 'success'
      }));
    } else {
      // Lock - require current location to be set
      if (!currentLocation) {
        setFormData((prev: InventoryFormData) => ({
          ...prev,
          validationMessage: t('warehouse.modules.inventory.errors.scanLocationBeforeLocking'),
          validationType: 'error'
        }));
        return;
      }
      setIsLocationLocked(true);
      setLockedLocation(currentLocation);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.messages.locationLocked', { '0': currentLocation.locationName }),
        validationType: 'success'
      }));
    }
    // Auto-focus disabled - global scanning is active
    // setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleItemSearchSelect = async (item: ItemsDto, location: LocationInfo) => {
    setShowItemSearchModal(false);

    // Set item and location from search modal
    setCurrentItem({
      itemNo: item.itemNo,
      description: item.description1 || '',
      itemType: item.itemType ?? undefined
    });
    setCurrentLocation({
      locationName: location.locationName
    });
    setFormData((prev: InventoryFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.inventory.messages.itemAndLocationSet', { '0': item.itemNo, '1': location.locationName, '2': location.availableQuantity }),
      validationType: 'success'
    }));

    // Focus will automatically go to quantity field
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  const handleLocationSearchSelect = (location: LocationSearchResult) => {
    setShowLocationSearchModal(false);

    // Set location
    setCurrentLocation({
      locationName: location.binCode
    });
    setFormData((prev: InventoryFormData) => ({
      ...prev,
      validationMessage: t('warehouse.modules.inventory.messages.locationSet', { '0': location.binCode }),
      validationType: 'success'
    }));

    // Only focus quantity field if item is already selected
    if (currentItem) {
      setTimeout(() => quantityInputRef.current?.focus(), 100);
    }
  };

  // Håndter serial-telling resultater
  const handleSerialCountComplete = async (results: SerialCountResult[]) => {
    if (!session || !currentItem || !currentLocation) return;
    setShowSerialCountingModal(false);
    setIsAdding(true);

    try {
      const newLines: InventoryCountLine[] = [];
      for (const result of results) {
        const countData = {
          itemNo: currentItem.itemNo,
          locationName: currentLocation.locationName,
          quantity: result.status === 'missing' ? 0 : 1,
          description1: currentItem.description,
          warehouse: session.warehouse,
          itemSerialNo: result.serialNo
        };

        const newLine = await inventoryService.addCountLine(session.batchID, countData);
        newLines.push(newLine);
      }

      // Oppdater session med alle nye linjer
      setSession((prev: InventorySession | null) => prev ? {
        ...prev,
        lines: [...newLines.reverse(), ...prev.lines],
        transactionCount: prev.transactionCount + newLines.length
      } : null);

      const confirmed = results.filter(r => r.status === 'confirmed').length;
      const missing = results.filter(r => r.status === 'missing').length;
      const newCount = results.filter(r => r.status === 'new').length;
      const parts: string[] = [];
      if (confirmed > 0) parts.push(`${confirmed} bekreftet`);
      if (missing > 0) parts.push(`${missing} mangler`);
      if (newCount > 0) parts.push(`${newCount} nye`);

      setFormData({
        scannedCode: '',
        quantity: '',
        isScanning: false,
        validationMessage: `Serienummer-telling fullført: ${parts.join(', ')}`,
        validationType: 'success'
      });
      setCurrentItem(null);
      setCurrentLocation(null);
    } catch (error) {
      console.error('Feil ved serial-telling:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: 'Feil ved lagring av serienummer-telling',
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  // Håndter batch-telling resultater
  const handleBatchCountComplete = async (results: BatchCountResult[]) => {
    if (!session || !currentItem || !currentLocation) return;
    setShowBatchCountingModal(false);
    setIsAdding(true);

    try {
      const newLines: InventoryCountLine[] = [];
      for (const result of results) {
        const countData = {
          itemNo: currentItem.itemNo,
          locationName: currentLocation.locationName,
          quantity: result.quantity,
          description1: currentItem.description,
          warehouse: session.warehouse,
          itemBatchNo: result.batchNo,
          itemBatchExpDate: result.expiryDate ? new Date(result.expiryDate) : undefined
        };

        const newLine = await inventoryService.addCountLine(session.batchID, countData);
        newLines.push(newLine);
      }

      // Oppdater session med alle nye linjer
      setSession((prev: InventorySession | null) => prev ? {
        ...prev,
        lines: [...newLines.reverse(), ...prev.lines],
        transactionCount: prev.transactionCount + newLines.length
      } : null);

      const confirmed = results.filter(r => r.status === 'confirmed').length;
      const missing = results.filter(r => r.status === 'missing').length;
      const newCount = results.filter(r => r.status === 'new').length;
      const parts: string[] = [];
      if (confirmed > 0) parts.push(`${confirmed} bekreftet`);
      if (missing > 0) parts.push(`${missing} mangler`);
      if (newCount > 0) parts.push(`${newCount} nye`);

      setFormData({
        scannedCode: '',
        quantity: '',
        isScanning: false,
        validationMessage: `Batch-telling fullført: ${parts.join(', ')}`,
        validationType: 'success'
      });
      setCurrentItem(null);
      setCurrentLocation(null);
    } catch (error) {
      console.error('Feil ved batch-telling:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: 'Feil ved lagring av batch-telling',
        validationType: 'error'
      }));
    } finally {
      setIsAdding(false);
    }
  };

  const handleCompleteSession = async () => {
    if (!session) return;

    if (session.lines.length === 0) {
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: t('warehouse.modules.inventory.errors.cannotCompleteEmptySession'),
        validationType: 'error'
      }));
      return;
    }

    const confirmComplete = window.confirm(t('warehouse.modules.inventory.confirmations.completeSession', { '0': session.lines.length }));
    if (!confirmComplete) return;

    try {
      const result = await inventoryService.completeInventorySession(session.batchID);
      if (result.success) {
        // Show appropriate message based on whether validation is required
        if (result.requiresValidation) {
          alert(t('warehouse.modules.inventory.messages.countSentForApproval'));
        } else {
          alert(t('warehouse.modules.inventory.messages.countCompletedAndExported'));
        }
        onBack(); // Gå tilbake til dashboard
      } else {
        throw new Error(result.message || 'Kunne ikke fullføre session');
      }
    } catch (error) {
      console.error('Feil ved fullføring:', error);
      setFormData((prev: InventoryFormData) => ({
        ...prev,
        validationMessage: error instanceof Error ? error.message : t('warehouse.modules.inventory.errors.completeSessionFailed'),
        validationType: 'error'
      }));
    }
  };

  // Render counting list mode (no session dependency)
  if (countingMode === 'counting-list') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="px-3 sm:px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <BackButton onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0" iconSize={20} label="" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{t('warehouse.modules.inventory.title')}</h1>
              </div>
            </div>
          </div>
          {/* Mode tabs */}
          <div className="px-3 sm:px-4 flex space-x-1 border-t bg-gray-50">
            <button
              onClick={() => { setCountingMode('free-counting'); setSelectedCountingList(null); }}
              className="px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700"
            >
              <span className="flex items-center space-x-1.5">
                <Scan className="h-4 w-4" />
                <span>Fritelling</span>
              </span>
            </button>
            <button
              className="px-3 py-2 text-sm font-medium border-b-2 border-blue-500 text-blue-600"
            >
              <span className="flex items-center space-x-1.5">
                <ClipboardList className="h-4 w-4" />
                <span>Telleliste</span>
              </span>
            </button>
          </div>
        </div>

        <div className="p-3 sm:p-4 max-w-4xl mx-auto">
          {selectedCountingList ? (
            <CountingListCounting
              list={selectedCountingList}
              onBack={() => setSelectedCountingList(null)}
              onCompleted={() => setSelectedCountingList(null)}
            />
          ) : (
            <CountingListSelector
              onSelectList={(list) => setSelectedCountingList(list)}
            />
          )}
        </div>
      </div>
    );
  }

  // Free counting mode
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin h-8 w-8 text-blue-500" />
        <span className="ml-2">{t('warehouse.modules.inventory.status.loadingSession')}</span>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-4 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-lg font-semibold mb-2">{t('warehouse.modules.inventory.errors.loadSessionFailed')}</h2>
        <button
          onClick={onBack}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          {t('common.buttons.backToDashboard')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile/Enterprise scanner support (Zebra, etc.) - invisible input that captures scanner data */}
      <MobileBarcodeCaptureInput
        enabled={barcodeScannerEnabled && !isAnyModalOpen}
        onScan={(barcode) => {
          console.log('📱 Mobile scanner detected barcode:', barcode);
          processGlobalBarcode(barcode);
        }}
      />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-3 sm:px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <BackButton onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0" iconSize={20} label="" />
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{t('warehouse.modules.inventory.title')}</h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">{session.description1}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
            <span className="text-xs sm:text-sm bg-blue-100 text-blue-800 px-1.5 sm:px-2 py-1 rounded whitespace-nowrap">
              {session.lines.length}
            </span>
            <MediaCaptureButton
              entityType="InventoryCounting"
              entityKey={session.batchID}
            />
            <button
              onClick={() => setShowSettingsModal(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px]"
              title={t('common.buttons.settings')}
            >
              <Settings className="h-5 w-5 text-gray-600" />
            </button>
            {session.lines.length > 0 && (
              <button
                onClick={handleCompleteSession}
                className="bg-green-500 text-white px-2 sm:px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm hover:bg-green-600 flex items-center space-x-1 min-h-[44px]"
              >
                <Save className="h-4 w-4" />
                <span className="hidden sm:inline">{t('common.buttons.complete')}</span>
              </button>
            )}
          </div>
        </div>
        {/* Mode tabs */}
        <div className="px-3 sm:px-4 flex space-x-1 border-t bg-gray-50">
          <button
            className="px-3 py-2 text-sm font-medium border-b-2 border-blue-500 text-blue-600"
          >
            <span className="flex items-center space-x-1.5">
              <Scan className="h-4 w-4" />
              <span>Fritelling</span>
            </span>
          </button>
          <button
            onClick={() => setCountingMode('counting-list')}
            className="px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700"
          >
            <span className="flex items-center space-x-1.5">
              <ClipboardList className="h-4 w-4" />
              <span>Telleliste</span>
            </span>
          </button>
        </div>
      </div>

      <div className="p-3 sm:p-4 max-w-4xl mx-auto space-y-3 sm:space-y-4">
        {/* Toolbar - hide when rapid scanning with locked location */}
        {!(enableRapidScanning && isLocationLocked) && (
          <div className="bg-white rounded-lg p-3 shadow-sm border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2">
                <Scan className="h-5 w-5 text-blue-500" />
                <span className="font-medium text-gray-700 text-sm sm:text-base">{t('warehouse.modules.inventory.quickSelect.title')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowItemSearchModal(true)}
                  className="flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm min-h-[44px] flex-1 sm:flex-initial"
                >
                  <Search className="h-4 w-4" />
                  <span>{t('warehouse.modules.inventory.quickSelect.searchItem')}</span>
                </button>
                <button
                  onClick={() => setShowLocationSearchModal(true)}
                  className="flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-purple-500 text-white hover:bg-purple-600 rounded-lg transition-colors text-sm min-h-[44px] flex-1 sm:flex-initial"
                  title={t('warehouse.modules.inventory.quickSelect.searchLocation')}
                >
                  <MapPin className="h-4 w-4" />
                  <span>{t('warehouse.modules.inventory.quickSelect.searchLocationShort')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Location lock status banner (only show when rapid scanning is enabled) */}
        {enableRapidScanning && isLocationLocked && lockedLocation && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-lg shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start sm:items-center min-w-0">
                <Lock className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5 sm:mt-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-blue-900 truncate">
                    {t('warehouse.modules.inventory.scanning.lockedTo', { '0': lockedLocation.locationName })}
                  </p>
                  <p className="text-xs text-blue-700">
                    {t('warehouse.modules.inventory.scanning.scanItemsToIncreaseQuantity')}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleLocationLock}
                className="flex items-center justify-center space-x-1 px-3 py-2.5 sm:py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm min-h-[44px] w-full sm:w-auto flex-shrink-0"
              >
                <Unlock className="h-4 w-4" />
                <span>{t('warehouse.modules.inventory.scanning.unlock')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Scanning og Input sektor */}
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border">
          <h2 className="font-semibold mb-3 flex items-center text-sm sm:text-base">
            <Scan className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0" />
            <span className="truncate">{t('warehouse.modules.inventory.scanning.scanItemOrLocation')}</span>
          </h2>

          <div className="space-y-3">
            {/* Scanner input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (formData.scannedCode.trim()) {
                  handleScanInput(formData.scannedCode.trim());
                  setFormData((prev: InventoryFormData) => ({ ...prev, scannedCode: '' }));
                }
              }}
              className="relative"
            >
              <input
                ref={scanInputRef}
                type="text"
                value={formData.scannedCode}
                onChange={(e) => setFormData((prev: InventoryFormData) => ({ ...prev, scannedCode: e.target.value }))}
                placeholder={
                  enableRapidScanning && isLocationLocked
                    ? t('warehouse.modules.inventory.scanning.scanItemForLocation', { '0': lockedLocation?.locationName || '' })
                    : t('warehouse.modules.inventory.scanning.scanBarcode')
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                disabled={formData.isScanning || showItemSearchModal || showLocationSearchModal}
                enterKeyHint="go"
              />
              {formData.isScanning && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader className="animate-spin h-5 w-5 text-blue-500" />
                </div>
              )}
            </form>

            {/* Validation message */}
            {formData.validationMessage && (
              <div className={`p-2 rounded flex items-start text-xs sm:text-sm ${
                formData.validationType === 'success'
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}>
                {formData.validationType === 'success' ? (
                  <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                )}
                <span className="break-words">{formData.validationMessage}</span>
              </div>
            )}

            {/* Current selections */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Current Item */}
              <div className={`p-3 border rounded-lg ${currentItem ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center">
                  <Package className={`h-4 w-4 mr-2 flex-shrink-0 ${currentItem ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className="font-medium text-sm">{t('warehouse.general.item')}</span>
                </div>
                {currentItem ? (
                  <div className="mt-1 min-w-0">
                    <div className="font-mono text-xs sm:text-sm truncate">{currentItem.itemNo}</div>
                    <div className="text-xs text-gray-600 truncate">{currentItem.description}</div>
                  </div>
                ) : (
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">{t('warehouse.modules.inventory.status.notScanned')}</div>
                )}
              </div>

              {/* Current Location */}
              <div className={`p-3 border rounded-lg ${
                isLocationLocked && lockedLocation
                  ? 'bg-blue-50 border-blue-300'
                  : currentLocation
                    ? 'bg-green-50 border-green-200'
                    : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0">
                    {isLocationLocked && lockedLocation ? (
                      <Lock className="h-4 w-4 mr-2 flex-shrink-0 text-blue-600" />
                    ) : (
                      <MapPin className={`h-4 w-4 mr-2 flex-shrink-0 ${currentLocation ? 'text-green-600' : 'text-gray-400'}`} />
                    )}
                    <span className="font-medium text-sm">{t('warehouse.general.location')}</span>
                  </div>
                  {/* Lock/Unlock button (only show when rapid scanning is enabled and location is set) */}
                  {enableRapidScanning && (currentLocation || lockedLocation) && (
                    <button
                      onClick={handleToggleLocationLock}
                      className={`p-1.5 rounded transition-colors flex-shrink-0 min-h-[36px] ${
                        isLocationLocked
                          ? 'text-blue-600 hover:bg-blue-100'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      title={isLocationLocked ? t('warehouse.modules.inventory.actions.unlockLocation') : t('warehouse.modules.inventory.actions.lockLocation')}
                    >
                      {isLocationLocked ? (
                        <Lock className="h-4 w-4" />
                      ) : (
                        <Unlock className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
                {(currentLocation || lockedLocation) ? (
                  <div className="mt-1 min-w-0">
                    <div className="font-mono text-xs sm:text-sm truncate">
                      {isLocationLocked && lockedLocation
                        ? lockedLocation.locationName
                        : currentLocation?.locationName}
                    </div>
                    {isLocationLocked && (
                      <div className="text-xs text-blue-600 mt-1">{t('warehouse.modules.inventory.status.locked')}</div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">{t('warehouse.modules.inventory.status.notScanned')}</div>
                )}
              </div>
            </div>

            {/* Serial counting button for ItemType 6 */}
            {currentItem && currentLocation && currentItem.itemType === 6 && !(enableRapidScanning && isLocationLocked) && (
              <div className="space-y-2">
                <button
                  onClick={() => setShowSerialCountingModal(true)}
                  disabled={isAdding}
                  className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-teal-700 transition-colors disabled:opacity-50 min-h-[44px]"
                >
                  {isAdding ? (
                    <Loader className="animate-spin h-4 w-4" />
                  ) : (
                    <Hash className="h-4 w-4" />
                  )}
                  <span>Tell serienumre</span>
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-600 flex items-center justify-center gap-2 min-h-[44px]"
                >
                  <RotateCcw className="h-4 w-4" />
                  <span>Nullstill</span>
                </button>
              </div>
            )}

            {/* Batch counting button for ItemType 4/5 */}
            {currentItem && currentLocation && (currentItem.itemType === 4 || currentItem.itemType === 5) && !(enableRapidScanning && isLocationLocked) && (
              <div className="space-y-2">
                <button
                  onClick={() => setShowBatchCountingModal(true)}
                  disabled={isAdding}
                  className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-orange-700 transition-colors disabled:opacity-50 min-h-[44px]"
                >
                  {isAdding ? (
                    <Loader className="animate-spin h-4 w-4" />
                  ) : (
                    <Package className="h-4 w-4" />
                  )}
                  <span>Tell batcher</span>
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-600 flex items-center justify-center gap-2 min-h-[44px]"
                >
                  <RotateCcw className="h-4 w-4" />
                  <span>Nullstill</span>
                </button>
              </div>
            )}

            {/* Quantity input og buttons - kun vis når både vare og lokasjon er skannet OG ikke i rapid scanning mode OG ikke serial/batch-vare */}
            {currentItem && currentLocation && !(currentItem.itemType === 4 || currentItem.itemType === 5 || currentItem.itemType === 6) && !(enableRapidScanning && isLocationLocked) && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (currentItem && currentLocation && formData.quantity.trim()) {
                    handleAddCount();
                  }
                }}
                className="space-y-2"
              >
                <div className="flex-1">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {t('common.fields.quantity')}
                  </label>
                  <input
                    ref={quantityInputRef}
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData((prev: InventoryFormData) => ({ ...prev, quantity: e.target.value }))}
                    placeholder={t('warehouse.modules.inventory.placeholders.enterQuantity')}
                    className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                    min="0.01"
                    step="0.01"
                    enterKeyHint="done"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="submit"
                    disabled={!currentItem || !currentLocation || !formData.quantity.trim() || isAdding}
                    className={`flex-1 px-4 py-2.5 rounded font-medium flex items-center justify-center space-x-2 text-sm min-h-[44px] ${
                      currentItem && currentLocation && formData.quantity.trim() && !isAdding
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isAdding ? (
                      <Loader className="animate-spin h-4 w-4" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    <span>{t('common.buttons.add')}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleReset}
                    className="p-2.5 border border-gray-300 rounded hover:bg-gray-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title={t('common.buttons.reset')}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Tellinger liste */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-3 sm:p-4 border-b">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center text-sm sm:text-base">
                <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 text-green-500" />
                <span className="truncate">
                  {t('warehouse.modules.inventory.lists.counts', { '0': session.lines.length })}
                  {filterText && (
                    <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500">
                      - {filteredLines.length}
                    </span>
                  )}
                </span>
              </h2>
            </div>

            {/* Filter input */}
            <div className="relative">
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={t('warehouse.modules.inventory.placeholders.filterItemsOrLocations')}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm min-h-[44px]"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              {filterText && (
                <button
                  onClick={() => setFilterText('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          {session.lines.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <Package className="h-10 sm:h-12 w-10 sm:w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm sm:text-base">{t('warehouse.modules.inventory.lists.noCountsYet')}</p>
              <p className="text-xs sm:text-sm">{t('warehouse.modules.inventory.lists.scanToStart')}</p>
            </div>
          ) : filteredLines.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <Search className="h-10 sm:h-12 w-10 sm:w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm sm:text-base">{t('warehouse.modules.inventory.lists.noCountsMatchFilter')}</p>
              <p className="text-xs sm:text-sm">{t('warehouse.modules.inventory.lists.tryDifferentSearch')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {filteredLines.map((line: InventoryCountLine) => (
                <div key={line.lineID} className="p-3 sm:p-4 hover:bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 gap-1 sm:gap-0">
                        <div className="font-mono text-xs sm:text-sm font-semibold truncate">
                          {line.itemNo}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 truncate">
                          {line.description1}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 mt-1 text-xs sm:text-sm text-gray-500">
                        <span className="flex items-center">
                          <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{line.locationName}</span>
                        </span>

                        {/* Serial number badge */}
                        {line.itemSerialNo && (
                          <span className="flex items-center text-purple-600">
                            <Hash className="h-3 w-3 mr-0.5 flex-shrink-0" />
                            <span className="font-mono">{line.itemSerialNo}</span>
                            {line.quantity === 0 && (
                              <span className="ml-1 text-red-500 font-medium">(mangler)</span>
                            )}
                          </span>
                        )}

                        {/* Batch number badge */}
                        {line.itemBatchNo && (
                          <span className="flex items-center text-orange-600">
                            <Package className="h-3 w-3 mr-0.5 flex-shrink-0" />
                            <span className="font-mono">{line.itemBatchNo}</span>
                            {line.quantity === 0 && (
                              <span className="ml-1 text-red-500 font-medium">(mangler)</span>
                            )}
                          </span>
                        )}

                        {/* Quantity - editable or display */}
                        {editingLine && editingLine.lineID === line.lineID ? (
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              handleSaveEdit();
                            }}
                            className="flex items-center space-x-1.5 sm:space-x-2"
                          >
                            <span>{t('warehouse.general.quantityShort')}:</span>
                            <input
                              ref={editQuantityInputRef}
                              type="number"
                              value={editingLine.quantity}
                              onChange={(e) => setEditingLine(prev => prev ? { ...prev, quantity: e.target.value } : null)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              className="w-16 sm:w-20 px-2 py-1.5 border border-blue-300 rounded text-xs sm:text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-h-[36px]"
                              min="0.01"
                              step="0.01"
                              enterKeyHint="done"
                            />
                            <div className="flex items-center space-x-1">
                              <button
                                type="submit"
                                disabled={updatingLineId === line.lineID}
                                className="p-1.5 text-green-600 hover:bg-green-100 rounded min-h-[36px] min-w-[36px] flex items-center justify-center"
                                title={t('common.buttons.save')}
                              >
                                {updatingLineId === line.lineID ? (
                                  <Loader className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Check className="h-3.5 w-3.5" />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded min-h-[36px] min-w-[36px] flex items-center justify-center"
                                title={t('common.buttons.cancel')}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </form>
                        ) : (
                          <span>{t('warehouse.general.quantityShort')}: {line.quantity}</span>
                        )}

                        <span className="whitespace-nowrap">{new Date(line.transDt).toLocaleTimeString('no-NO')}</span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs whitespace-nowrap">
                        {line.statusText}
                      </span>

                      {/* Action buttons - kun vis hvis ikke i edit mode */}
                      {!editingLine && (
                        <div className="flex items-center space-x-1">
                          {/* Skjul redigering for serial/batch-linjer */}
                          {!line.itemSerialNo && !line.itemBatchNo && (
                          <button
                            onClick={() => handleStartEdit(line)}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded min-h-[36px] min-w-[36px] flex items-center justify-center"
                            title={t('warehouse.modules.inventory.actions.editQuantity')}
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          )}
                          <button
                            onClick={() => handleDeleteLine(line)}
                            disabled={deletingLineId === line.lineID}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded min-h-[36px] min-w-[36px] flex items-center justify-center"
                            title={t('warehouse.modules.inventory.actions.deleteCount')}
                          >
                            {deletingLineId === line.lineID ? (
                              <Loader className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ItemSearchModal
        isOpen={showItemSearchModal}
        onClose={() => setShowItemSearchModal(false)}
        onSelect={handleItemSearchSelect}
        title={t('warehouse.modules.inventory.modals.searchAndSelectItem')}
        fetchLocations={async (itemNo: string) => {
          const locations = await inventoryService.getLocationsForItem(itemNo);
          return locations.map((loc: ItemLocationInfo) => ({
            locationName: loc.locationName,
            warehouseID: loc.warehouseID || '',
            availableQuantity: loc.availableQuantity
          }));
        }}
      />

      <LocationSearchModal
        isOpen={showLocationSearchModal}
        onClose={() => setShowLocationSearchModal(false)}
        onSelect={handleLocationSearchSelect}
        title={t('warehouse.modules.inventory.modals.searchLocation')}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        enableRapidScanning={enableRapidScanning}
        onEnableRapidScanningChange={setEnableRapidScanning}
        onClose={() => setShowSettingsModal(false)}
      />

      {currentItem && currentLocation && (
        <SerialCountingModal
          isOpen={showSerialCountingModal}
          onClose={() => setShowSerialCountingModal(false)}
          onComplete={handleSerialCountComplete}
          itemNo={currentItem.itemNo}
          itemDescription={currentItem.description}
          binCode={currentLocation.locationName}
          warehouseId={session?.warehouse || ''}
        />
      )}

      {currentItem && currentLocation && (
        <BatchCountingModal
          isOpen={showBatchCountingModal}
          onClose={() => setShowBatchCountingModal(false)}
          onComplete={handleBatchCountComplete}
          itemNo={currentItem.itemNo}
          itemDescription={currentItem.description}
          binCode={currentLocation.locationName}
          warehouseId={session?.warehouse || ''}
          itemType={currentItem.itemType || 0}
        />
      )}
    </div>
  );
};